import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb, Button, Card, Collapse, Input, Spin, Tabs } from "antd";
import { DoubleLeftOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import KycService from "../../services/kyc-service";
import { createKycTileLoadingMap, fetchKycSeparateTileCounts } from "./kycTileCountApi";
import KycTileSpin from "./KycTileSpin";
import {
	buildKycCompanyTabUrl,
	buildKycDashboardReturnLink,
	buildKycMaterialsTableSearchReturn,
	buildKycPartnerFunctionMaterialTabReturn,
	KYC_TILE_COUNT_KEYS,
	KYC_DEFAULT_MATERIAL_DETAILS_TAB,
	KYC_MATERIAL_DETAILS_TAB_LABELS,
	KYC_MATERIAL_DETAILS_TAB_PARAM,
	kycBreadcrumbLabelsMatch,
	loadKycMaterialNavContext,
	resolveKycNavField,
	resolveKycNavReturnTab,
	resolveMaterialDetailsTab,
	saveKycMaterialNavContext,
	getKycAppliedFilterPayload,
	KYC_EMPTY_GLOBAL_FILTER_DATE,
} from "./kycHelpers";
import KycMaterialDesignTab from "./KycMaterialDesignTab";
import KycMaterialQuotationTab from "./KycMaterialQuotationTab";
import KycMaterialOrdersTab from "./KycMaterialOrdersTab";
import KycMaterialQualityIssueTab from "./KycMaterialQualityIssueTab";
import KycMaterialCustomerTab from "./KycMaterialCustomerTab";
import "./KYC-Dashboard.css";
import KycStickyTopBar from "./KycStickyTopBar";
import {
	KYCBusinessOverviewImage,
	KYCCylindersImage,
	KYCDesignImage,
} from "../Elite/imageAssets";

const { Search } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const defaultPayload = {
	fromDate: KYC_EMPTY_GLOBAL_FILTER_DATE,
	toDate: KYC_EMPTY_GLOBAL_FILTER_DATE,
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const hasDisplayCount = (value) => {
	if (value === undefined || value === null || value === "") {
		return false;
	}
	const numericValue = Number(value);
	if (!Number.isNaN(numericValue) && numericValue === 0) {
		return false;
	}
	return true;
};

const getCardTitle = (countObj, label, keys = ["totalCount", "total"]) => {
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? `${countValue} ${label}` : `- ${label}`;
};

const getCardCountValue = (countObj, keys = []) => {
	if (!countObj || Object.keys(countObj).length === 0) {
		return "-";
	}
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? countValue : "-";
};

const MATERIAL_TILES = [
	{
		key: "design",
		bgClass: "kycbg_grey",
		bodyClass: "KYCDesign",
		icon: KYCDesignImage,
		title: "Designs",
		first: { keys: ["runningCount", "running"], label: "Running" },
		second: { keys: ["hiddenCount", "hide"], label: "Hide" },
	},
	{
		key: "quotation",
		bgClass: "kycbg_blue",
		bodyClass: "KYCBusinessOverview",
		icon: KYCBusinessOverviewImage,
		title: "Quotation",
	},
	{
		key: "qualityIssue",
		bgClass: "kycbg_green",
		bodyClass: "KYCBusinessOverview",
		icon: KYCBusinessOverviewImage,
		title: "Quality Issue",
		first: { keys: ["closeCount", "closedCount", "close", "closed"], label: "Close" },
		second: { keys: ["openCount", "pendingCount", "open"], label: "Open" },
	},
	{
		key: "cylinder",
		bgClass: "kycbg_purple",
		bodyClass: "KYCCylinders",
		icon: KYCCylindersImage,
		title: "Cylinders",
		first: { keys: ["runningCount", "running"], label: "Running" },
		second: { keys: ["hiddenCount", "hide", "close"], label: "Close" },
	},
];

const MATERIAL_TILE_COUNT_KEYS = [
	KYC_TILE_COUNT_KEYS.design,
	KYC_TILE_COUNT_KEYS.quotation,
	KYC_TILE_COUNT_KEYS.quality,
	KYC_TILE_COUNT_KEYS.cylinder,
];

const MATERIAL_TILE_API_KEY = {
	design: KYC_TILE_COUNT_KEYS.design,
	quotation: KYC_TILE_COUNT_KEYS.quotation,
	qualityIssue: KYC_TILE_COUNT_KEYS.quality,
	cylinder: KYC_TILE_COUNT_KEYS.cylinder,
};

const FIELD_BINDINGS = [
	["AD LAMI GPM", "ispl_adlamigpm"],
	["AD LAMI GSM", "ispl_adlamigsm"],
	["Ad Lami Material", "ispl_adlamimaterial"],
	["AD LAMI MATERIAL GPM", "ispl_adlamimaterialgpm"],
	["AD LAMI MATERIAL GSM", "ispl_adlamimaterialgsm"],
	["AD LAMI MATERIAL WIDTH", "ispl_adlamimaterialwidth"],
	["Ad Lami Micron", "ispl_adlamimicron"],
	["AD LAMI MKG", "ispl_adlamimkg"],
	["AD LAMI WEIGHT", "ispl_adlamiweight"],
	["ADHESIVE GSM", "ispl_adhesivegsm"],
	["AUTO GSM", "ispl_autogsm"],
	["BACK SEAM PASTE", "ispl_backseampaste"],
	["BACK SEAM SEALING AREA", "ispl_backseamsealingarea"],
	["Bag Cut Length (MM)", "ispl_bagcutlengthmm"],
	["BAG LENGTH + PATCH", "ispl_baglengthpatch"],
	["BAG OPEN WIDTH", "ispl_bagopenwidth"],
	["BAG WEIGHT IN CUT", "ispl_bagweightincut"],
	["Bag/Pouch Size", "ispl_bagpouchsize"],
	["Bags in One Bunch (Manual)", "ispl_bagsinonebunchmanual"],
	["Bottom & Top Patch Height", "ispl_bottomtappatchheight"],
	["BOTTOM PATCH AD LAMI GSM", "ispl_bottompatchadlamigsm"],
	["BOTTOM PATCH LENGTH", "ispl_bottompatchlength"],
	["BOTTOM PATCH PRINTED ROLL GSM", "ispl_bottompatchprintedrollgsm"],
	["BOTTOM PATCH WEIGHT", "ispl_bottompatchweight"],
	["BOTTOM PATCH WIDTH", "ispl_bottompatchwidth"],
	["Bottom Stitch Type", "ispl_bottomstitchtype"],
	["BOTTOM STITCHING THREAD WT", "ispl_bottomstitchingthreadwt"],
	["BOTTOM TOTAL PATCH GSM", "ispl_bottomtotalpatchgsm"],
	["Card Board/Bag in Pallet", "ispl_cardboardbaginpallet"],
	["Core Closer", "ispl_corecloser"],
	["Core Type", "ispl_coretype"],
	["CRIP TAPE WEIGHT", "ispl_criptapeweight"],
	["Cutting Type", "ispl_cuttingtype"],
	["Cylinder Circum (MM)", "ispl_cylindercircummm"],
	["Cylinder Engraving Type", "ispl_cylinderengravingtype"],
	["Cylinder Width (MM)", "ispl_cylinderwidthmm"],
	["Cylinders Front Back Printing", "ispl_cylinderfrontbackprinting"],
	["D-METALIZE GPM", "ispl_dmetalizegpm"],
	["D-METALIZE GSM", "ispl_dmetalizegsm"],
	["D-METALIZE MKG", "ispl_dmetalizemkg"],
	["Design Width (MM)", "ispl_designwidthmm"],
	["Division", "ispl_division"],
	["EACH PER KG", "ispl_eachperkg"],
	["Ex Lami Coating", "ispl_exlamicoating"],
	["EX LAMI GPM", "ispl_exlamigpm"],
	["Ex Lami GSM", "ispl_exlamigsm"],
	["EX LAMI MICRON", "ispl_exlamimicron"],
	["EX LAMI MKG", "ispl_exlamimkg"],
	["Ex Lami Type", "ispl_exlamitype"],
	["EX LAMI WEIGHT", "ispl_exlamiweight"],
	["EX LAMI WIDTH", "ispl_exlamiwidth"],
	["EXTENDED SIZE", "ispl_extendedsize"],
	["Fabric Colour", "ispl_fabriccolour"],
	["Fabric Denier", "ispl_fabricdenier"],
	["Fabric Elongation/Warp-V (%)", "ispl_fabricelongationwarpv"],
	["Fabric Elongation/Weft-H (%)", "ispl_fabricelongationwefth"],
	["Fabric Material", "ispl_fabricmaterial"],
	["Fabric Opacity", "ispl_fabricopacity"],
	["Fabric Ring Size", "ispl_fabricringsize"],
	["Fabric Strength/Warp (V)", "ispl_fabricstrengthwarpv"],
	["Fabric Strength/Weft (H)", "ispl_fabricstrengthwefth"],
	["Fabric Type", "ispl_fabrictype"],
	["Fabric Weaving Type", "ispl_fabricweavingtype"],
	["Fabric Weight", "ispl_fabricweight"],
	["Fabric Width", "ispl_fabricwidth"],
	["Fabric Width (Production)", "ispl_fabricwidthproduction"],
	["FILLER (%)", "ispl_filler"],
	["FINISH PRODUCT FOLDER NO", "ispl_finishproductfolderno"],
	["FLAT GLUE WEIGHT", "ispl_flatglueweight"],
	["Flexo Gusset Printing", "ispl_flexogussetprinting"],
	["Flexo Printing", "ispl_flexoprinting"],
	["Front Width (MM)", "ispl_frontwidthmm"],
	["GLUE REQ AT CUSTOMER/OPEN PINCH", "ispl_gluereqatcustomeropenpinch"],
	["Gusset (MM)", "ispl_gussetmm"],
	["Gusset Type", "ispl_gussettype"],
	["HAMMING THREAD WT", "ispl_hammingthreadwt"],
	["Handle", "ispl_handle"],
	["HANDLE CUT LENGTH", "ispl_handlecutlength"],
	["Handle Material", "ispl_handlematerial"],
	["Handle Weight", "ispl_handleweight"],
	["Handle Width", "ispl_handlewidth"],
	["Hierarchy 1", "ispl_hierarchy1"],
	["Hierarchy 2", "ispl_hierarchy2"],
	["Hierarchy 3", "ispl_hierarchy3"],
	["HOT PASTING PINCH BOTTOM", "ispl_hotpastingpinchbottom"],
	["INK GSM", "ispl_inkgsm"],
	["LAMINATED FABRIC GPM", "ispl_laminatedfabricgpm"],
	["LAMINATED FABRIC GSM", "ispl_laminatedfabricgsm"],
	["LAMINATED FABRIC WEIGHT", "ispl_laminatedfabricweight"],
	["LAMINATION DIRECTION", "ispl_laminationdirection"],
	["Length (ID MM)", "ispl_lengthidmm"],
	["Liner", "ispl_liner"],
	["LINER CUT LENGTH", "ispl_linercutlength"],
	["Liner Gauge", "ispl_linergauge"],
	["LINER GSM", "ispl_linergsm"],
	["Liner Length (ID)", "ispl_linerlengthid"],
	["Liner Material", "ispl_linermaterial"],
	["LINER MICRON", "ispl_linermicron"],
	["LINER ROLL GPM", "ispl_linerrollgpm"],
	["LINER ROLL GSM", "ispl_linerrollgsm"],
	["Liner Seal Size", "ispl_linersealsize"],
	["LINER TYPE", "ispl_linertype"],
	["LINER WEIGHT", "ispl_linerweight"],
	["Liner Width", "ispl_linerwidth"],
	["LL-LDPE (LINER)", "ispl_llldpelinerrp"],
	["Manual Cyl Width (+/- MM)", "ispl_manualcylwidthmm"],
	["Manual Design Width (+/- MM)", "ispl_manualdesignwidthmm"],
	["Marketing Weight", "ispl_marketingweight"],
	["MIXED RP", "ispl_mixedrp"],
	["No of Bag in One Bunch", "ispl_noofbaginonebunch"],
	["No of Repeats", "ispl_noofrepeats"],
	["NO OF STITCH (100MM)", "ispl_noofstitch100mm"],
	["No of Tapes", "ispl_nooftapes"],
	["NWF COLORS", "ispl_nwfcolors"],
	["NWF DENIER", "ispl_nwfdenier"],
	["NWF GPM", "ispl_nwfgpm"],
	["NWF GSM", "ispl_nwfgsm"],
	["NWF MATERIAL", "ispl_nwfmaterial"],
	["NWF WIDTH (MM)", "ispl_nwfwidthmm"],
	["ONLINE CYLINDER CIRCUM (MM)", "ispl_onlinecylindercircummm"],
	["Ownership of Cylinder/Stereo", "ispl_ownershipofcylinderstereo"],
	["PACKING BALE HEIGHT", "ispl_packingbaleheight"],
	["PACKING BALE LENGTH", "ispl_packingbalelength"],
	["PACKING BALE WEIGHT", "ispl_packingbaleweight"],
	["PACKING BALE WIDTH", "ispl_packingbalewidth"],
	["Packing Type", "ispl_packingtype"],
	["PATCH LENGTH + EXTENDED 20 MM", "ispl_patchlengthextended20mm"],
	["Perforation", "ispl_perforation"],
	["PINCH BOTTOM SEAL TYPE", "ispl_pintchbottomsealtype"],
	["Pinch/Bottom Gusset (MM)", "ispl_pinchbottomgussetmm"],
	["PP (TAPE LINE AND LOOMS) RP", "ispl_pptapelineandloomsrp"],
	["PP-LD (LAMINATION) RP", "ispl_ppldlaminationrp"],
	["PRINTED FILM GPM", "ispl_printedfilmgpm"],
	["PRINTED FILM GSM", "ispl_printedfilmgsm"],
	["PRINTED FILM WEIGHT", "ispl_printedfilmweight"],
	["PRINTING DESIGN WIDTH", "ispl_printingdesignwidth"],
	["PRINTING DIRECTION", "ispl_printingdirection"],
	["Printing Material", "ispl_printingmaterial"],
	["Printing Material Micron", "ispl_printingmaterialmicron"],
	["PRINTING MATERIAL WIDTH", "ispl_printingmaterialwidth"],
	["PRINTING MKG", "ispl_printingmkg"],
	["Printing Side", "ispl_kprintingside"],
	["PULL TAPE WEIGHT", "ispl_pulltapeweight"],
	["Remarks01", "ispl_remarks01"],
	["Remarks02", "ispl_remarks02"],
	["Remarks03", "ispl_remarks03"],
	["Remarks04", "ispl_remarks04"],
	["REPEAT LENGTH", "ispl_repeatlength"],
	["Screen Printing", "ispl_screenprinting"],
	["Screen Printing Side", "ispl_screenprintingside"],
	["SLITING MKG", "ispl_slitingmkg"],
	["Stereo Front Back Printing", "ispl_stereofrontbackprinting"],
	["Stereo Length", "ispl_stereolength"],
	["Stereo Material Type", "ispl_stereomaterialtype"],
	["Stereo Width", "ispl_stereowidth"],
	["Stitch Chain Type", "ispl_stitchchaintype"],
	["Structure", "ispl_structure"],
	["TAPE COLOUR", "ispl_tapecolour"],
	["TAPE DENIER", "ispl_tapedenier"],
	["TAPE ELONGATION", "ispl_tapeelongation"],
	["TAPE FOLDER NO", "ispl_tapefolderno"],
	["TAPE GPD", "ispl_tapegpd"],
	["TAPE MARKING", "ispl_tapemarking"],
	["TAPE MATERIAL", "ispl_tapematerial"],
	["TAPE SHRINKAGE", "ispl_tapeshrinkage"],
	["TAPE STRENGTH (KGF)", "ispl_tapestrengthkgf"],
	["TAPE WIDTH", "ispl_tapewidth"],
	["TOP PATCH AD LAMI GSM", "ispl_toppatchadlamigsm"],
	["TOP PATCH PRINTED ROLL GSM", "ispl_toppatchprintedrollgsm"],
	["TOP PATCH WEIGHT", "ispl_toppatchwaight"],
	["TOP PATCH WIDTH", "ispl_toppatchwidth"],
	["Top Stitch Type", "ispl_topstitchtype"],
	["TOP TOTAL PATCH GSM", "ispl_toptotalpatchgsm"],
	["Total Bag in One Bale/Pallet", "ispl_totalbaginonebalepallet"],
	["Total Piece in One Cycle", "ispl_totalpieceinonecycle"],
	["Total Width (MM)", "ispl_totalwidthmm"],
	["TYPE OF BAG", "ispl_typeofbag"],
	["U-TYPE WEIGHT", "ispl_utypeweight"],
	["Undercoat F/B Side", "ispl_undercoatfbside"],
	["UNDERCOAT WIDTH", "ispl_undercoatwidth"],
	["UNPRINTED FILM GPM", "ispl_unprintedfilmgpm"],
	["UNPRINTED FILM GSM", "ispl_unprintedfilmgsm"],
	["Upside/Down Cylinder", "ispl_upscoilofcylinder"],
	["UV", "ispl_uv"],
	["VALVE (H) = PATCH (H) × 2 ÷ 20", "ispl_valvehpatchhx220"],
	["VALVE AD LAMI GSM", "ispl_valveadlamigsm"],
	["VALVE DEPTH (LENGTH) MM", "ispl_valvedepthlengthmm"],
	["Valve Position", "ispl_valveposition"],
	["VALVE PRINTED ROLL GSM", "ispl_valveprintedrollgsm"],
	["VALVE TOTAL GSM = FABRIC + FL", "ispl_valvetotalgsmfabricfl"],
	["VM WT. = VALVE TOTAL (GSM × VA)", "ispl_vmwtvalvetotalgsmxva"],
	["WEIGHT OF PIECE", "ispl_weightofpiece"],
	["Window Type", "ispl_windowtype"],
	["YARN COLOUR", "ispl_yarncolour"],
	["YARN DENIER", "ispl_yarndenier"],
	["YARN ELONGATION", "ispl_yarnelongation"],
	["YARN FOLDER NO.", "ispl_yarnfolderno"],
	["YARN GPD", "ispl_yarngpd"],
	["YARN MARKING", "ispl_yarnmarking"],
	["YARN MATERIAL", "ispl_yarnmaterial"],
	["YARN SHRINKAGE", "ispl_yarnshrinkage"],
	["YARN STRENGTH", "ispl_yarnstrength"],
	["YARN TPM", "ispl_yarntpm"],
	["Fabric GSM", "ispl_fabricgsm"],
	["UV Percentage (%)", "ispl_uvpercentage"],
];

const resolveFieldValue = (record, key) => {
	if (!record || !key) {
		return "-";
	}
	const fallbackMap = {
		ispl_division: ["ispl_divisiontext"],
		ispl_structure: ["ispl_structuretext"],
		ispl_hierarchy1: ["ispl_hierarchy1text"],
		ispl_hierarchy2: ["ispl_hierarchy2text"],
		ispl_hierarchy3: ["ispl_hierarchy3text"],
	};
	const candidates = [key, ...(fallbackMap[key] || [])];
	for (const candidate of candidates) {
		const value = record?.[candidate];
		if (value === null || value === undefined || value === "") {
			continue;
		}
		const stringValue = String(value);
		if (stringValue.startsWith("Microsoft.Xrm")) {
			continue;
		}
		return stringValue;
	}
	return "-";
};

const fieldHasDisplayableData = (record, key) => {
	const displayValue = resolveFieldValue(record, key);
	if (displayValue === "-") {
		return false;
	}
	return String(displayValue).trim() !== "";
};

const getVisibleCharacteristicBindings = (record, search) => {
	const query = (search || "").trim().toLowerCase();
	return FIELD_BINDINGS.filter(([label, key]) => {
		if (!key) {
			return false;
		}
		if (!fieldHasDisplayableData(record, key)) {
			return false;
		}
		return !query || label.toLowerCase().includes(query) || key.toLowerCase().includes(query);
	});
};

const splitIntoColumns = (items, columnCount) => {
	const columns = Array.from({ length: columnCount }, () => []);
	items.forEach((item, index) => {
		columns[index % columnCount].push(item);
	});
	return columns;
};

const LABEL_ACRONYMS = new Set([
	"GSM",
	"GPM",
	"MKG",
	"MM",
	"ID",
	"UV",
	"PP",
	"NWF",
	"TPM",
	"GPD",
	"KGF",
	"SAP",
	"CRM",
	"HSN",
	"RP",
	"LL",
	"LDPE",
	"FB",
]);

const formatFieldLabel = (label) => {
	const rawLabel = String(label || "").trim().replace(/\s+/g, " ");
	if (!rawLabel) {
		return "";
	}

	return rawLabel.replace(/[A-Za-z]+/g, (word) => {
		const upper = word.toUpperCase();
		if (LABEL_ACRONYMS.has(upper)) {
			return upper;
		}
		return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
	});
};

const FIELD_LABEL_BY_KEY = new Map(
	FIELD_BINDINGS.map(([label, key]) => [key, label])
);

const formatKeyAsLabel = (key) => {
	if (FIELD_LABEL_BY_KEY.has(key)) {
		return FIELD_LABEL_BY_KEY.get(key);
	}
	const stripped = String(key || "")
		.replace(/^ispl_/i, "")
		.replace(/_/g, " ");
	return formatFieldLabel(stripped);
};

const getVisibleFieldsForGroup = (groupData, search) => {
	const query = (search || "").trim().toLowerCase();

	return Object.keys(groupData || {})
		.filter((key) => fieldHasDisplayableData(groupData, key))
		.filter((key) => {
			if (!query) {
				return true;
			}
			const label = formatKeyAsLabel(key);
			return (
				label.toLowerCase().includes(query) || key.toLowerCase().includes(query)
			);
		})
		.map((key) => [formatKeyAsLabel(key), key]);
};

const parseGroupsFromRecord = (record, search) => {
	if (!record) {
		return [];
	}

	if (!Array.isArray(record.groups) || record.groups.length === 0) {
		const fallbackFields = getVisibleCharacteristicBindings(record, search);
		if (!fallbackFields.length) {
			return [];
		}
		return [
			{
				groupName: "Material Characteristics",
				groupKey: "material-characteristics",
				groupData: record,
				fields: fallbackFields,
			},
		];
	}

	return record.groups
		.map((groupItem, index) => {
			const groupName = Object.keys(groupItem || {})[0];
			if (!groupName) {
				return null;
			}

			const groupData = groupItem[groupName] || {};
			const fields = getVisibleFieldsForGroup(groupData, search);
			if (!fields.length) {
				return null;
			}

			return {
				groupName,
				groupKey: `${groupName}-${index}`,
				groupData,
				fields,
			};
		})
		.filter(Boolean);
};

const CharacteristicsAccordion = ({ record, fields, search }) => {
	const visibleBindings = useMemo(() => {
		if (fields?.length) {
			return fields;
		}
		return getVisibleCharacteristicBindings(record, search);
	}, [record, fields, search]);
	const columns = useMemo(() => splitIntoColumns(visibleBindings, 4), [visibleBindings]);

	if (visibleBindings.length === 0) {
		return null;
	}

	return (
		<div className="row kyc-mc-accordion-body">
			{columns.map((column, colIdx) => (
				<div className="col-lg-3 col-md-6 kyc-mc-column" key={`col-${colIdx}`}>
					{column.map(([label, key]) => (
						<div className="kyc-mc-field " key={key}>
							<div className="kyc-mc-field-label">{formatFieldLabel(label)}:</div>
							<div className="kyc-mc-field-value">{resolveFieldValue(record, key)}</div>
						</div>
					))}
				</div>
			))}
		</div>
	);
};

const KycMaterialCharacteristicsDetails = () => {
	const { product } = useParams();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const loginDetails = useSelector((state) => state.loginReducer.loginDetials.userDetails);
	const [isLoading, setIsLoading] = useState(false);
	const tileCountsRequestIdRef = useRef(0);
	const [tileLoading, setTileLoading] = useState(() =>
		createKycTileLoadingMap(MATERIAL_TILE_COUNT_KEYS, false)
	);
	const [charSearch, setCharSearch] = useState("");
	const [debouncedCharSearch, setDebouncedCharSearch] = useState("");
	const [characteristicsLists, setCharacteristicsLists] = useState([]);
	const [activePanels, setActivePanels] = useState([]);
	const [designsCount, setDesignsCount] = useState({});
	const [quotationCount, setQuotationCount] = useState({});
	const [qualityIssueCount, setQualityIssueCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});

	const activeTab = useMemo(
		() => resolveMaterialDetailsTab(searchParams.get(KYC_MATERIAL_DETAILS_TAB_PARAM)),
		[searchParams]
	);

	const productName = useMemo(() => {
		if (!product) {
			return "";
		}
		try {
			return decodeURIComponent(product);
		} catch {
			return product;
		}
	}, [product]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [productName]);

	useEffect(() => {
		const timer = window.setTimeout(() => {
			setDebouncedCharSearch(charSearch);
		}, 400);
		return () => window.clearTimeout(timer);
	}, [charSearch]);

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycMaterialNavContext(productName);

		return {
			materialName: fromState.materialName || stored.materialName || productName,
			groupCompanyId: resolveKycNavField(fromState, stored, "groupCompanyId"),
			groupCompanyName: resolveKycNavField(fromState, stored, "groupCompanyName"),
			materialsCustomer: resolveKycNavField(fromState, stored, "materialsCustomer"),
			accountName: resolveKycNavField(fromState, stored, "accountName"),
			returnTab: resolveKycNavReturnTab(fromState, stored, "materials"),
			partnerCrmGuid: resolveKycNavField(fromState, stored, "partnerCrmGuid"),
		};
	}, [location.state, productName]);

	const kycFilterPayload = useMemo(
		() => getKycAppliedFilterPayload(loginDetails),
		[loginDetails]
	);

	const isplCustomer = String(
		navContext.materialsCustomer || navContext.accountName || ""
	).trim();

	useEffect(() => {
		if (!productName) {
			return undefined;
		}

		const requestId = tileCountsRequestIdRef.current + 1;
		tileCountsRequestIdRef.current = requestId;
		setTileLoading(createKycTileLoadingMap(MATERIAL_TILE_COUNT_KEYS, true));
		fetchKycSeparateTileCounts(
			{
				...defaultPayload,
				...kycFilterPayload,
			},
			MATERIAL_TILE_COUNT_KEYS,
			{
				groupCompanyId: navContext.groupCompanyId,
				isplCustomer,
				isplProduct: productName,
			},
			(tileKey, data) => {
				if (tileCountsRequestIdRef.current !== requestId) {
					return;
				}
				if (tileKey === KYC_TILE_COUNT_KEYS.design) {
					setDesignsCount(data || {});
				} else if (tileKey === KYC_TILE_COUNT_KEYS.quotation) {
					setQuotationCount(data || {});
				} else if (tileKey === KYC_TILE_COUNT_KEYS.quality) {
					setQualityIssueCount(data || {});
				} else if (tileKey === KYC_TILE_COUNT_KEYS.cylinder) {
					setCylindersCount(data || {});
				}
				setTileLoading((prev) => ({ ...prev, [tileKey]: false }));
			}
		).catch((error) => {
			if (tileCountsRequestIdRef.current !== requestId) {
				return;
			}
			console.log(error, "Error");
			setTileLoading(createKycTileLoadingMap(MATERIAL_TILE_COUNT_KEYS, false));
		});
		return undefined;
	}, [productName, kycFilterPayload, isplCustomer, navContext.groupCompanyId]);

	const tileCounts = {
		design: designsCount,
		quotation: quotationCount,
		qualityIssue: qualityIssueCount,
		cylinder: cylindersCount,
	};

	useEffect(() => {
		if (!productName || activeTab !== KYC_DEFAULT_MATERIAL_DETAILS_TAB) {
			return undefined;
		}

		const {
			materialName,
			groupCompanyId,
			groupCompanyName,
			materialsCustomer,
			accountName,
			returnTab,
			partnerCrmGuid,
		} = navContext;
		if (!materialName && !groupCompanyName && !materialsCustomer && !partnerCrmGuid) {
			return;
		}

		saveKycMaterialNavContext(productName, {
			materialName: materialName || productName,
			groupCompanyId,
			groupCompanyName,
			materialsCustomer,
			accountName,
			returnTab,
			partnerCrmGuid,
		});
	}, [productName, navContext]);

	const returnTab = navContext.returnTab || "materials";
	const groupCompanyBreadcrumbLabel = (navContext.groupCompanyName || "").trim();
	const materialsCustomerLabel = (navContext.materialsCustomer || "").trim();
	const accountBreadcrumbLabel = (navContext.accountName || materialsCustomerLabel || "").trim();
	const materialBreadcrumbLabel = navContext.materialName || productName || "Material";

	const normalizedGroupLabel = groupCompanyBreadcrumbLabel.toLowerCase();
	const normalizedAccountLabel = accountBreadcrumbLabel.toLowerCase();
	const hasDistinctGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel !== normalizedAccountLabel;
	const showGroupInBreadcrumb = Boolean(
		groupCompanyBreadcrumbLabel && (returnTab === "materials" || hasDistinctGroupAndAccount)
	);
	const showMaterialsInBreadcrumb = Boolean(returnTab === "materials" && materialsCustomerLabel);
	const showAccountInBreadcrumb = Boolean(accountBreadcrumbLabel && returnTab === "account");
	const groupAndAccountNamesMatch = kycBreadcrumbLabelsMatch(
		groupCompanyBreadcrumbLabel,
		showAccountInBreadcrumb ? accountBreadcrumbLabel : ""
	);

	const dashboardGroupCompanyLink = useMemo(() => {
		if (!showGroupInBreadcrumb) {
			return "/kyc";
		}
		return buildKycDashboardReturnLink({
			returnTab: "groupCompany",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
		});
	}, [showGroupInBreadcrumb, groupCompanyBreadcrumbLabel, navContext.groupCompanyId]);

	const dashboardMaterialsDrillAccountLink = useMemo(() => {
		if (!showMaterialsInBreadcrumb) {
			return null;
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: showGroupInBreadcrumb ? groupCompanyBreadcrumbLabel : "",
			accountName: materialsCustomerLabel,
		});
	}, [
		showMaterialsInBreadcrumb,
		showGroupInBreadcrumb,
		groupCompanyBreadcrumbLabel,
		materialsCustomerLabel,
		navContext.groupCompanyId,
	]);

	const dashboardAccountLink = useMemo(() => {
		if (!showAccountInBreadcrumb) {
			return buildKycCompanyTabUrl("account");
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
			accountName: accountBreadcrumbLabel,
		});
	}, [
		showAccountInBreadcrumb,
		accountBreadcrumbLabel,
		groupCompanyBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const drillAccountName = navContext.accountName.trim() || navContext.materialsCustomer.trim();
	const dashboardMaterialsReturn = useMemo(
		() =>
			buildKycMaterialsTableSearchReturn({
				materialName: materialBreadcrumbLabel,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: drillAccountName,
			}),
		[navContext, drillAccountName, materialBreadcrumbLabel]
	);
	const partnerFunctionMaterialReturn = useMemo(
		() =>
			buildKycPartnerFunctionMaterialTabReturn({
				crmGuid: navContext.partnerCrmGuid,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: accountBreadcrumbLabel,
				materialsCustomer: materialsCustomerLabel || accountBreadcrumbLabel,
				returnTab,
			}),
		[
			navContext.partnerCrmGuid,
			navContext.groupCompanyId,
			navContext.groupCompanyName,
			accountBreadcrumbLabel,
			materialsCustomerLabel,
			returnTab,
		]
	);

	const dashboardBackNavigation = useMemo(() => {
		if (navContext.partnerCrmGuid) {
			return {
				to: partnerFunctionMaterialReturn.link,
				state: partnerFunctionMaterialReturn.state,
			};
		}
		if (returnTab === "account") {
			return {
				to: buildKycDashboardReturnLink({
					returnTab: "account",
					groupCompanyId: navContext.groupCompanyId,
					groupCompanyName: groupCompanyBreadcrumbLabel,
					accountName: accountBreadcrumbLabel,
				}),
			};
		}
		return {
			to: dashboardMaterialsReturn.link,
			state: dashboardMaterialsReturn.state,
		};
	}, [
		navContext.partnerCrmGuid,
		navContext.groupCompanyId,
		returnTab,
		groupCompanyBreadcrumbLabel,
		accountBreadcrumbLabel,
		dashboardMaterialsReturn,
		partnerFunctionMaterialReturn,
	]);

	const activeTabLabel =
		KYC_MATERIAL_DETAILS_TAB_LABELS[activeTab] || KYC_MATERIAL_DETAILS_TAB_LABELS.characteristics;

	const handleTabChange = (nextTab) => {
		const resolvedTab = resolveMaterialDetailsTab(nextTab);
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (!resolvedTab || resolvedTab === KYC_DEFAULT_MATERIAL_DETAILS_TAB) {
					next.delete(KYC_MATERIAL_DETAILS_TAB_PARAM);
				} else {
					next.set(KYC_MATERIAL_DETAILS_TAB_PARAM, resolvedTab);
				}
				return next;
			},
			{ replace: true }
		);
	};

	useEffect(() => {
		if (!productName || activeTab !== KYC_DEFAULT_MATERIAL_DETAILS_TAB) {
			if (activeTab !== KYC_DEFAULT_MATERIAL_DETAILS_TAB) {
				setIsLoading(false);
			}
			return undefined;
		}

		setIsLoading(true);
		const requestData = {
			pageindex: 1,
			pagesize: 50,
			sortcolumn: "createdon",
			orderBy: "DESC",
			search: "",
			...defaultPayload,
			...getKycAppliedFilterPayload(),
			ispl_product: productName,
		};

		KycService.getKycMaterialCharacteristicsListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data.responseData || {};
					const list = Array.isArray(responseData)
						? responseData
						: responseData.kycMaterialCharacteristicsLists ||
						responseData.kycMaterialCharacteristicsList ||
						responseData.data ||
						[];
					setCharacteristicsLists(Array.isArray(list) ? list : []);
				} else {
					NotificationManager.error(response.data?.message || "Unable to load material characteristics.");
					setCharacteristicsLists([]);
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load material characteristics.");
				setCharacteristicsLists([]);
			})
			.finally(() => {
				setIsLoading(false);
			});
		return undefined;
	}, [productName, activeTab]);

	const visibleGroups = useMemo(() => {
		const groups = [];

		characteristicsLists.forEach((row, rowIndex) => {
			const recordKey = row.id || row.ispl_materialchacterid || String(rowIndex);
			parseGroupsFromRecord(row, debouncedCharSearch).forEach((group) => {
				groups.push({
					...group,
					groupKey: `${recordKey}-${group.groupKey}`,
					recordKey,
				});
			});
		});

		return groups;
	}, [characteristicsLists, debouncedCharSearch]);

	useEffect(() => {
		setActivePanels(visibleGroups.map((group) => group.groupKey));
	}, [visibleGroups]);

	const handleCollapseChange = (keys) => {
		if (Array.isArray(keys)) {
			setActivePanels(keys);
			return;
		}
		setActivePanels(keys ? [keys] : []);
	};

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<KycStickyTopBar
							variant="details"
							className="kyc-dashboard-top-bar details_view_title kyc-details-top-bar">
							<div className="kyc-dashboard-top-bar__left">
								<div className="kyc-dashboard-top-bar__breadcrumb">
									<Breadcrumb className="details_breadcrum" separator={">"}>
										<Breadcrumb.Item>
											<Link to="/kyc">Dashboard</Link>
										</Breadcrumb.Item>
										{showGroupInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardGroupCompanyLink}>
													<label>{groupCompanyBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showMaterialsInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardMaterialsDrillAccountLink}>
													<label>{materialsCustomerLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showAccountInBreadcrumb && !groupAndAccountNamesMatch ? (
											<Breadcrumb.Item>
												<Link to={dashboardAccountLink}>
													<label>{accountBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										<Breadcrumb.Item>
											<Link
												to={
													navContext.partnerCrmGuid
														? partnerFunctionMaterialReturn.link
														: dashboardMaterialsReturn.link
												}
												state={
													navContext.partnerCrmGuid
														? partnerFunctionMaterialReturn.state
														: dashboardMaterialsReturn.state
												}
												onDoubleClick={(event) => {
													event.preventDefault();
												}}>
												<label>{materialBreadcrumbLabel}</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{activeTabLabel}</label>
										</Breadcrumb.Item>
									</Breadcrumb>
								</div>
							</div>
							<div className="kyc-dashboard-top-bar__actions">
								<Link
									to={dashboardBackNavigation.to}
									state={dashboardBackNavigation.state}>
									<Button type="primary" className="back_btn" icon={<DoubleLeftOutlined />}>
										Back
									</Button>
								</Link>
							</div>
						</KycStickyTopBar>

						<div className="row kyc-mc-tiles-row mb-3">
								{MATERIAL_TILES.map((tile) => {
									const counts = tileCounts[tile.key] || {};
									const tileApiKey = MATERIAL_TILE_API_KEY[tile.key];
									return (
										<div className="col-xl-4 col-lg-6 col-12" key={tile.key}>
											<div className={`card ${tile.bgClass}`}>
												<KycTileSpin spinning={tileLoading[tileApiKey]}>
												<div className="card-content">
													<div className={`kyc-card-body ${tile.bodyClass} images_bg`}>
														<div className="kyc-card-header">
															<div className="kyc-card-title">
																<img src={tile.icon} alt="Img" />
																<span>{getCardTitle(counts, tile.title)}</span>
															</div>
														</div>
														{tile.first && tile.second ? (
															<div className="kyc-card-stats">
																<div className="card_counts text-left">
																	<h5 className="text-white mb-2">
																		{getCardCountValue(counts, tile.first.keys)}
																	</h5>
																	<h6 className="text-white mb-0">{tile.first.label}</h6>
																</div>
																<div className="kyc-border-right-white border-right-lighten-3"></div>
																<div className="card_counts text-left">
																	<h5 className="text-white mb-2">
																		{getCardCountValue(counts, tile.second.keys)}
																	</h5>
																	<h6 className="text-white mb-0">{tile.second.label}</h6>
																</div>
															</div>
														) : null}
													</div>
												</div>
												</KycTileSpin>
											</div>
										</div>
									);
								})}
							</div>
							<Card className="kyc-table-card">
								<div className="kyc-table-header">
									<h5 className="kyc-company-title">Material Details</h5>
									<div className="tab_searchtxt">
										<Search
											placeholder="Search..."
											className="dash_search"
											allowClear
											size="large"
											value={charSearch}
											onChange={(e) => setCharSearch(e.target.value)}
										/>
									</div>
								</div>
								<Tabs
									activeKey={activeTab}
									className="dash_tab_panel kyc_company_tabs"
									type="card"
									destroyInactiveTabPane
									onChange={handleTabChange}>
									<TabPane tab="Material Characteristics" key="characteristics">
										<Spin spinning={isLoading}>
										{characteristicsLists.length === 0 && !isLoading ? (
											<div className="kyc-tab-placeholder">No material characteristics found for this product.</div>
										) : visibleGroups.length === 0 && !isLoading ? (
											<div className="kyc-tab-placeholder">
												{charSearch.trim()
													? "No fields with data match your search."
													: "No characteristic fields have data for this product."}
											</div>
										) : (
											<Collapse
												activeKey={activePanels}
												onChange={handleCollapseChange}
												className="kyc-mc-collapse">
												{visibleGroups.map((group) => (
													<Panel
														showArrow={false}
														header={
															<span className="kyc-mc-panel-header">
																<span className="kyc-mc-panel-title">
																	{group.groupName}
																</span>
																<span
																	className={`kyc-mc-expand-icon ${activePanels.includes(group.groupKey)
																			? "active"
																			: ""
																		}`}>
																	{activePanels.includes(group.groupKey) ? (
																		<MinusOutlined />
																	) : (
																		<PlusOutlined />
																	)}
																</span>
															</span>
														}
														key={group.groupKey}>
														<CharacteristicsAccordion
															record={group.groupData}
															fields={group.fields}
															search={charSearch}
														/>
													</Panel>
												))}
											</Collapse>
										)}
										</Spin>
									</TabPane>
									<TabPane tab="Quotation" key="quotation">
										<KycMaterialQuotationTab
											productName={productName}
											search={debouncedCharSearch}
											isActive={activeTab === "quotation"}
											navigationContext={navContext}
										/>
									</TabPane>
									<TabPane tab="Orders" key="orders">
										<KycMaterialOrdersTab
											productName={productName}
											search={debouncedCharSearch}
											isActive={activeTab === "orders"}
											navigationContext={navContext}
										/>
									</TabPane>
									<TabPane tab="Design" key="design">
										<KycMaterialDesignTab
											productName={productName}
											search={debouncedCharSearch}
											isActive={activeTab === "design"}
											navigationContext={navContext}
										/>
									</TabPane>
									{/* <TabPane tab="Customer" key="customer">
										<KycMaterialCustomerTab
											productName={productName}
											search={debouncedCharSearch}
											isActive={activeTab === "customer"}
											navigationContext={navContext}
										/>
									</TabPane> */}
									{/* <TabPane tab="Cardiogram" key="cardiogram">
										<div className="kyc-tab-placeholder">Cardiogram data will appear here once the API is ready.</div>
									</TabPane> */}
									<TabPane tab="Quality Issue" key="quality">
										<KycMaterialQualityIssueTab
											productName={productName}
											search={debouncedCharSearch}
											isActive={activeTab === "quality"}
											navigationContext={navContext}
										/>
									</TabPane>
								</Tabs>
							</Card>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycMaterialCharacteristicsDetails;
